/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { TextField, Typography, Grid2, Container, Button } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import PropTypes from "prop-types";
import {
  fetchAdditionalDocumentDetails,
  fetchDeleteAdditionalDocument,
} from "../../Services/redux/AdditionalDocument/AdditionalDocumentActions";
import PopupDialog from "../PopupDialog";
import AddIcon from "@mui/icons-material/Add";
import "./ApplicationDetailsAdditional.css";
import { fetchSubmissionDetails } from "../../Services/redux/Application-details/ApplicationDetailsActions";

// Reusable No Data Placeholder SVG Component
export const NoDataPlaceholder = () => (
  <svg
    width="100%"
    height="147"
    viewBox="0 0 1149 147"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      width="1148"
      height="147"
      transform="translate(0.5)"
      fill="#F3F3F3"
    />
    <rect
      x="529.688"
      y="16"
      width="90"
      height="90"
      rx="45"
      fill="#E8E8E8"
    />
    <rect
      opacity="0.5"
      x="490.688"
      y="46"
      width="31"
      height="31"
      rx="15.5"
      fill="#E8E8E8"
    />
    <rect
      opacity="0.5"
      x="627.688"
      y="46"
      width="31"
      height="31"
      rx="15.5"
      fill="#E8E8E8"
    />
    <rect
      opacity="0.5"
      x="665.938"
      y="50.75"
      width="21.5"
      height="21.5"
      rx="10.75"
      fill="#E8E8E8"
    />
    <rect
      opacity="0.5"
      x="461.562"
      y="50.5"
      width="21.5"
      height="21.5"
      rx="10.75"
      fill="#E8E8E8"
    />
    <rect
      x="541.841"
      y="39.6401"
      width="42"
      height="51"
      rx="3"
      transform="rotate(-6.73863 541.841 39.6401)"
      fill="#121415"
      fillOpacity="0.2"
    />
    <g filter="url(#filter0_d_1929_30611)">
      <rect
        x="561.188"
        y="31.75"
        width="42"
        height="51"
        rx="3"
        fill="#E8E8E8"
      />
    </g>
    <rect
      x="588.629"
      y="71.0566"
      width="3"
      height="9"
      transform="rotate(-30 588.629 71.0566)"
      fill="#121415"
      fillOpacity="0.2"
    />
    <rect
      x="588.83"
      y="74.4048"
      width="6"
      height="19.5"
      rx="3"
      transform="rotate(-30 588.83 74.4048)"
      fill="#414344"
    />
    <rect
      x="562.688"
      y="53.125"
      width="29.25"
      height="29.25"
      rx="14.625"
      transform="rotate(-30 562.688 53.125)"
      fill="#A0A1A1"
    />
    <path
      d="M581.075 55.6309L579.156 55.6309C579.146 54.6885 579.469 53.9097 580.123 53.2944C580.777 52.6792 581.639 52.3716 582.708 52.3716C583.397 52.3716 583.983 52.4985 584.466 52.7524C584.95 53.0063 585.335 53.3726 585.624 53.8511C585.917 54.3296 586.063 54.8325 586.063 55.3599C586.063 55.648 586.017 55.9312 585.924 56.2095C585.831 56.4878 585.685 56.7734 585.484 57.0664C585.367 57.2373 585.086 57.5254 584.642 57.9307C584.217 58.3164 583.951 58.5898 583.844 58.751C583.736 58.9072 583.646 59.1172 583.573 59.3809C583.5 59.6445 583.453 60.0107 583.434 60.4795L581.449 60.4795L581.441 60.1499C581.441 59.4956 581.561 58.9023 581.8 58.3701C582.04 57.833 582.428 57.3301 582.965 56.8613C583.482 56.4072 583.795 56.0947 583.902 55.9238C584.01 55.7529 584.063 55.5527 584.063 55.3232C584.063 55.0107 583.932 54.7471 583.668 54.5322C583.409 54.3125 583.06 54.2026 582.621 54.2026C582.171 54.2026 581.803 54.3369 581.515 54.6055C581.227 54.8691 581.08 55.2109 581.075 55.6309ZM582.474 61.3145C582.831 61.3145 583.133 61.4365 583.382 61.6807C583.636 61.9248 583.763 62.2202 583.763 62.5669C583.763 62.9136 583.639 63.2114 583.39 63.4604C583.141 63.7046 582.84 63.8267 582.489 63.8267C582.137 63.8267 581.837 63.7046 581.588 63.4604C581.344 63.2114 581.222 62.9136 581.222 62.5669C581.222 62.2202 581.344 61.9248 581.588 61.6807C581.832 61.4365 582.127 61.3145 582.474 61.3145Z"
      fill="#414344"
    />
    <path
      d="M539.553 118.469V127H538.416L534.121 120.42V127H532.99V118.469H534.121L538.434 125.066V118.469H539.553ZM541.225 123.9V123.766C541.225 123.309 541.292 122.885 541.425 122.494C541.557 122.1 541.749 121.758 541.999 121.469C542.249 121.176 542.552 120.949 542.907 120.789C543.263 120.625 543.661 120.543 544.102 120.543C544.548 120.543 544.948 120.625 545.304 120.789C545.663 120.949 545.968 121.176 546.218 121.469C546.472 121.758 546.665 122.1 546.798 122.494C546.931 122.885 546.997 123.309 546.997 123.766V123.9C546.997 124.357 546.931 124.781 546.798 125.172C546.665 125.562 546.472 125.904 546.218 126.197C545.968 126.486 545.665 126.713 545.309 126.877C544.958 127.037 544.559 127.117 544.114 127.117C543.669 127.117 543.268 127.037 542.913 126.877C542.557 126.713 542.253 126.486 541.999 126.197C541.749 125.904 541.557 125.562 541.425 125.172C541.292 124.781 541.225 124.357 541.225 123.9ZM542.309 123.766V123.9C542.309 124.217 542.347 124.516 542.421 124.797C542.495 125.074 542.606 125.32 542.755 125.535C542.907 125.75 543.097 125.92 543.323 126.045C543.55 126.166 543.813 126.227 544.114 126.227C544.411 126.227 544.671 126.166 544.893 126.045C545.12 125.92 545.307 125.75 545.456 125.535C545.604 125.32 545.716 125.074 545.79 124.797C545.868 124.516 545.907 124.217 545.907 123.9V123.766C545.907 123.453 545.868 123.158 545.79 122.881C545.716 122.6 545.602 122.352 545.45 122.137C545.302 121.918 545.114 121.746 544.888 121.621C544.665 121.496 544.403 121.434 544.102 121.434C543.806 121.434 543.544 121.496 543.317 121.621C543.095 121.746 542.907 121.918 542.755 122.137C542.606 122.352 542.495 122.6 542.421 122.881C542.347 123.158 542.309 123.453 542.309 123.766ZM554.046 127H552.264L552.276 126.08H554.046C554.655 126.08 555.163 125.953 555.569 125.699C555.975 125.441 556.28 125.082 556.483 124.621C556.69 124.156 556.794 123.613 556.794 122.992V122.471C556.794 121.982 556.735 121.549 556.618 121.17C556.501 120.787 556.329 120.465 556.102 120.203C555.876 119.938 555.598 119.736 555.27 119.6C554.946 119.463 554.573 119.395 554.151 119.395H552.229V118.469H554.151C554.71 118.469 555.219 118.562 555.68 118.75C556.141 118.934 556.538 119.201 556.87 119.553C557.206 119.9 557.464 120.322 557.643 120.818C557.823 121.311 557.913 121.865 557.913 122.482V122.992C557.913 123.609 557.823 124.166 557.643 124.662C557.464 125.154 557.204 125.574 556.864 125.922C556.528 126.27 556.122 126.537 555.645 126.725C555.173 126.908 554.639 127 554.046 127ZM552.868 118.469V127H551.737V118.469H552.868ZM563.306 125.916V122.652C563.306 122.402 563.255 122.186 563.154 122.002C563.056 121.814 562.908 121.67 562.709 121.568C562.509 121.467 562.263 121.416 561.97 121.416C561.697 121.416 561.457 121.463 561.25 121.557C561.046 121.65 560.886 121.773 560.769 121.926C560.656 122.078 560.599 122.242 560.599 122.418H559.515C559.515 122.191 559.574 121.967 559.691 121.744C559.808 121.521 559.976 121.32 560.195 121.141C560.417 120.957 560.683 120.812 560.992 120.707C561.304 120.598 561.652 120.543 562.035 120.543C562.496 120.543 562.902 120.621 563.253 120.777C563.609 120.934 563.886 121.17 564.085 121.486C564.289 121.799 564.39 122.191 564.39 122.664V125.617C564.39 125.828 564.408 126.053 564.443 126.291C564.482 126.529 564.539 126.734 564.613 126.906V127H563.482C563.427 126.875 563.384 126.709 563.353 126.502C563.322 126.291 563.306 126.096 563.306 125.916ZM563.494 123.156L563.505 123.918H562.41C562.101 123.918 561.826 123.943 561.584 123.994C561.341 124.041 561.138 124.113 560.974 124.211C560.81 124.309 560.685 124.432 560.599 124.58C560.513 124.725 560.47 124.895 560.47 125.09C560.47 125.289 560.515 125.471 560.605 125.635C560.695 125.799 560.83 125.93 561.009 126.027C561.193 126.121 561.417 126.168 561.683 126.168C562.015 126.168 562.308 126.098 562.562 125.957C562.816 125.816 563.017 125.645 563.166 125.441C563.318 125.238 563.4 125.041 563.412 124.85L563.875 125.371C563.847 125.535 563.773 125.717 563.652 125.916C563.531 126.115 563.369 126.307 563.166 126.49C562.966 126.67 562.728 126.82 562.451 126.941C562.177 127.059 561.869 127.117 561.525 127.117C561.095 127.117 560.718 127.033 560.394 126.865C560.074 126.697 559.824 126.473 559.644 126.191C559.468 125.906 559.38 125.588 559.38 125.236C559.38 124.896 559.447 124.598 559.58 124.34C559.712 124.078 559.904 123.861 560.154 123.689C560.404 123.514 560.705 123.381 561.056 123.291C561.408 123.201 561.8 123.156 562.234 123.156H563.494ZM568.87 120.66V121.492H565.442V120.66H568.87ZM566.602 119.119H567.686V125.43C567.686 125.645 567.719 125.807 567.786 125.916C567.852 126.025 567.938 126.098 568.043 126.133C568.149 126.168 568.262 126.186 568.383 126.186C568.473 126.186 568.567 126.178 568.664 126.162C568.766 126.143 568.842 126.127 568.893 126.115L568.899 127C568.813 127.027 568.7 127.053 568.559 127.076C568.422 127.104 568.256 127.117 568.061 127.117C567.795 127.117 567.551 127.064 567.329 126.959C567.106 126.854 566.928 126.678 566.795 126.432C566.666 126.182 566.602 125.846 566.602 125.424V119.119ZM573.999 125.916V122.652C573.999 122.402 573.949 122.186 573.847 122.002C573.749 121.814 573.601 121.67 573.402 121.568C573.202 121.467 572.956 121.416 572.663 121.416C572.39 121.416 572.15 121.463 571.943 121.557C571.74 121.65 571.579 121.773 571.462 121.926C571.349 122.078 571.292 122.242 571.292 122.418H570.208C570.208 122.191 570.267 121.967 570.384 121.744C570.501 121.521 570.669 121.32 570.888 121.141C571.111 120.957 571.376 120.812 571.685 120.707C571.997 120.598 572.345 120.543 572.728 120.543C573.189 120.543 573.595 120.621 573.947 120.777C574.302 120.934 574.579 121.17 574.779 121.486C574.982 121.799 575.083 122.191 575.083 122.664V125.617C575.083 125.828 575.101 126.053 575.136 126.291C575.175 126.529 575.232 126.734 575.306 126.906V127H574.175C574.12 126.875 574.077 126.709 574.046 126.502C574.015 126.291 573.999 126.096 573.999 125.916ZM574.187 123.156L574.199 123.918H573.103C572.794 123.918 572.519 123.943 572.277 123.994C572.034 124.041 571.831 124.113 571.667 124.211C571.503 124.309 571.378 124.432 571.292 124.58C571.206 124.725 571.163 124.895 571.163 125.09C571.163 125.289 571.208 125.471 571.298 125.635C571.388 125.799 571.523 125.93 571.702 126.027C571.886 126.121 572.111 126.168 572.376 126.168C572.708 126.168 573.001 126.098 573.255 125.957C573.509 125.816 573.71 125.645 573.859 125.441C574.011 125.238 574.093 125.041 574.105 124.85L574.568 125.371C574.54 125.535 574.466 125.717 574.345 125.916C574.224 126.115 574.062 126.307 573.859 126.49C573.659 126.67 573.421 126.82 573.144 126.941C572.87 127.059 572.562 127.117 572.218 127.117C571.788 127.117 571.411 127.033 571.087 126.865C570.767 126.697 570.517 126.473 570.337 126.191C570.161 125.906 570.074 125.588 570.074 125.236C570.074 124.896 570.14 124.598 570.273 124.34C570.406 124.078 570.597 123.861 570.847 123.689C571.097 123.514 571.398 123.381 571.749 123.291C572.101 123.201 572.493 123.156 572.927 123.156H574.187ZM581.3 118.469V127H580.169V118.469H581.3ZM584.874 122.307V123.232H581.054V122.307H584.874ZM585.454 118.469V119.395H581.054V118.469H585.454ZM586.342 123.9V123.766C586.342 123.309 586.408 122.885 586.541 122.494C586.674 122.1 586.865 121.758 587.115 121.469C587.365 121.176 587.668 120.949 588.023 120.789C588.379 120.625 588.777 120.543 589.219 120.543C589.664 120.543 590.064 120.625 590.42 120.789C590.779 120.949 591.084 121.176 591.334 121.469C591.588 121.758 591.781 122.1 591.914 122.494C592.047 122.885 592.113 123.309 592.113 123.766V123.9C592.113 124.357 592.047 124.781 591.914 125.172C591.781 125.562 591.588 125.904 591.334 126.197C591.084 126.486 590.781 126.713 590.426 126.877C590.074 127.037 589.676 127.117 589.23 127.117C588.785 127.117 588.385 127.037 588.029 126.877C587.674 126.713 587.369 126.486 587.115 126.197C586.865 125.904 586.674 125.562 586.541 125.172C586.408 124.781 586.342 124.357 586.342 123.9ZM587.426 123.766V123.9C587.426 124.217 587.463 124.516 587.537 124.797C587.611 125.074 587.723 125.32 587.871 125.535C588.023 125.75 588.213 125.92 588.439 126.045C588.666 126.166 588.93 126.227 589.23 126.227C589.527 126.227 589.787 126.166 590.01 126.045C590.236 125.92 590.424 125.75 590.572 125.535C590.721 125.32 590.832 125.074 590.906 124.797C590.984 124.516 591.023 124.217 591.023 123.9V123.766C591.023 123.453 590.984 123.158 590.906 122.881C590.832 122.6 590.719 122.352 590.566 122.137C590.418 121.918 590.23 121.746 590.004 121.621C589.781 121.496 589.519 121.434 589.219 121.434C588.922 121.434 588.66 121.496 588.434 121.621C588.211 121.746 588.023 121.918 587.871 122.137C587.723 122.352 587.611 122.6 587.537 122.881C587.463 123.158 587.426 123.453 587.426 123.766ZM597.471 125.535V120.66H598.561V127H597.524L597.471 125.535ZM597.677 124.199L598.128 124.188C598.128 124.609 598.083 125 597.993 125.359C597.907 125.715 597.766 126.023 597.571 126.285C597.376 126.547 597.12 126.752 596.804 126.9C596.487 127.045 596.102 127.117 595.649 127.117C595.341 127.117 595.057 127.072 594.8 126.982C594.546 126.893 594.327 126.754 594.143 126.566C593.96 126.379 593.817 126.135 593.716 125.834C593.618 125.533 593.569 125.172 593.569 124.75V120.66H594.653V124.762C594.653 125.047 594.684 125.283 594.747 125.471C594.813 125.654 594.901 125.801 595.011 125.91C595.124 126.016 595.249 126.09 595.386 126.133C595.526 126.176 595.671 126.197 595.819 126.197C596.28 126.197 596.645 126.109 596.915 125.934C597.184 125.754 597.378 125.514 597.495 125.213C597.616 124.908 597.677 124.57 597.677 124.199ZM601.418 122.014V127H600.334V120.66H601.359L601.418 122.014ZM601.16 123.59L600.709 123.572C600.713 123.139 600.777 122.738 600.902 122.371C601.027 122 601.203 121.678 601.429 121.404C601.656 121.131 601.925 120.92 602.238 120.771C602.554 120.619 602.904 120.543 603.287 120.543C603.599 120.543 603.881 120.586 604.131 120.672C604.381 120.754 604.593 120.887 604.769 121.07C604.949 121.254 605.086 121.492 605.179 121.785C605.273 122.074 605.32 122.428 605.32 122.846V127H604.23V122.834C604.23 122.502 604.181 122.236 604.084 122.037C603.986 121.834 603.843 121.688 603.656 121.598C603.468 121.504 603.238 121.457 602.965 121.457C602.695 121.457 602.449 121.514 602.226 121.627C602.007 121.74 601.818 121.896 601.658 122.096C601.502 122.295 601.379 122.523 601.289 122.781C601.203 123.035 601.16 123.305 601.16 123.59ZM611.083 125.77V118H612.172V127H611.176L611.083 125.77ZM606.817 123.9V123.777C606.817 123.293 606.876 122.854 606.993 122.459C607.114 122.061 607.284 121.719 607.503 121.434C607.725 121.148 607.989 120.93 608.294 120.777C608.602 120.621 608.946 120.543 609.325 120.543C609.723 120.543 610.071 120.613 610.368 120.754C610.669 120.891 610.922 121.092 611.129 121.357C611.34 121.619 611.506 121.936 611.628 122.307C611.749 122.678 611.833 123.098 611.879 123.566V124.105C611.836 124.57 611.753 124.988 611.628 125.359C611.506 125.73 611.34 126.047 611.129 126.309C610.922 126.57 610.669 126.771 610.368 126.912C610.067 127.049 609.715 127.117 609.313 127.117C608.942 127.117 608.602 127.037 608.294 126.877C607.989 126.717 607.725 126.492 607.503 126.203C607.284 125.914 607.114 125.574 606.993 125.184C606.876 124.789 606.817 124.361 606.817 123.9ZM607.907 123.777V123.9C607.907 124.217 607.938 124.514 608.001 124.791C608.067 125.068 608.169 125.312 608.305 125.523C608.442 125.734 608.616 125.9 608.827 126.021C609.038 126.139 609.29 126.197 609.583 126.197C609.942 126.197 610.237 126.121 610.467 125.969C610.702 125.816 610.889 125.615 611.03 125.365C611.17 125.115 611.28 124.844 611.358 124.551V123.139C611.311 122.924 611.243 122.717 611.153 122.518C611.067 122.314 610.954 122.135 610.813 121.979C610.676 121.818 610.506 121.691 610.303 121.598C610.104 121.504 609.868 121.457 609.594 121.457C609.297 121.457 609.042 121.52 608.827 121.645C608.616 121.766 608.442 121.934 608.305 122.148C608.169 122.359 608.067 122.605 608.001 122.887C607.938 123.164 607.907 123.461 607.907 123.777ZM615.269 118.469L615.193 124.592H614.214L614.132 118.469H615.269ZM614.097 126.455C614.097 126.279 614.15 126.131 614.255 126.01C614.365 125.889 614.525 125.828 614.736 125.828C614.943 125.828 615.101 125.889 615.21 126.01C615.324 126.131 615.38 126.279 615.38 126.455C615.38 126.623 615.324 126.768 615.21 126.889C615.101 127.01 614.943 127.07 614.736 127.07C614.525 127.07 614.365 127.01 614.255 126.889C614.15 126.768 614.097 126.623 614.097 126.455Z"
      fill="#414344"
    />
    <defs>
      <filter
        id="filter0_d_1929_30611"
        x="558.938"
        y="31.75"
        width="44.25"
        height="54"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dx="-1.5" dy="2.25" />
        <feGaussianBlur stdDeviation="0.375" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_1929_30611"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_1929_30611"
          result="shape"
        />
      </filter>
    </defs>
  </svg>
);

function ApplicationDetailsAdditionalDocument({
  postCallTheNewSubmission,
  submissionData,
  appId,
}) {
  const [additionalText, setAdditionalText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [dimensionError, setDimensionError] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [componentName, setComponentName] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const appDetails = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  // Validation helpers
  const isSubmissionValid = () => submissionData && Object.keys(submissionData).length > 0;
  const isDraftSubmission = () => isSubmissionValid() && submissionData.status === "DRAFT";
  const hasAttachment = () => isSubmissionValid() && submissionData?.additionalInformation?.attachment;
  const isNonDraftWithInfo = () => isSubmissionValid() && submissionData.status !== "DRAFT";

  const shouldShowReadOnlyMessage = (data) => {
    return isSubmissionValid() &&
      isNonDraftWithInfo() &&
      data.additionalInformation &&
      Object.keys(data.additionalInformation).length > 0 &&
      data.additionalInformation.comment?.length > 0;
  };

  // Event handlers
  const handleCloseDialog = () => setOpenErrorPopup(false);

  const handleButtonClick = () => fileInputRef.current.click();

  const initializeFromSubmission = () => {
    if (!isSubmissionValid() || !submissionData.additionalInformation) return;
    
    const { comment, attachment } = submissionData.additionalInformation;
    setAdditionalText(comment || "");
    
    if (attachment) {
      setSelectedFile(attachment);
      setSelectedFileName(attachment.fileName);
    }
  };

  const handleFileSizeError = (errorMsg) => {
    setDimensionError(errorMsg);
    setHeaderMessage(errorMsg);
    setComponentName("appError");
    setOpenErrorPopup(true);
  };

  const dispatchFileUpload = (file) => {
    dispatch(fetchAdditionalDocumentDetails(appId, submissionData.id, file));
    setTimeout(() => {
      dispatch(fetchSubmissionDetails(appId, submissionData.id));
    }, 1000);
  };

  const clearAttachmentState = () => {
    setSelectedFile(null);
    setSelectedFileName("");
  };

  const dispatchDeleteAndReload = () => {
    dispatch(
      fetchDeleteAdditionalDocument(
        appDetails.id,
        submissionData.id,
        submissionData?.additionalInformation?.attachment?.fileName
      )
    );
    clearAttachmentState();
    setTimeout(() => {
      dispatch(fetchSubmissionDetails(appDetails.id, submissionData.id));
    }, 1000);
  };

  // Initialize submission data on mount
  useEffect(() => {
    initializeFromSubmission();
  }, []);

  // Form submission handler
  const handleSubmit = ($event) => {
    const newSubmissionData = { ...submissionData };
    const commentValue = $event.target.value;
    
    if (newSubmissionData.additionalInformation?.comment) {
      newSubmissionData.additionalInformation.comment = commentValue;
    } else {
      newSubmissionData.additionalInformation = { attachment: null, comment: commentValue };
    }
    
    delete newSubmissionData.legalDocuments;
    postCallTheNewSubmission(newSubmissionData);
  };

  // File upload handler
  const handleAdditionalDocumentChange = ($event) => {
    const file = $event.target.files[0];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    if (file.size > maxSize) {
      handleFileSizeError("File size exceeds the limit (5MB)");
      return;
    }
    setSelectedFile(file)
    dispatchFileUpload(file);
  };

  // Delete attachment handler
  const deleteAttachment = () => {
    const hasValidAppDetails = appDetails && Object.keys(appDetails).length > 0;
    if (hasValidAppDetails && hasAttachment()) {
      dispatchDeleteAndReload();
    }
  };

  // Rendering helpers
  const renderAdditionalInfoContent = () => {
    if (isDraftSubmission()) {
      return (
        <TextField
          sx={{
            "&.MuiFormControl-root": { width: "100%" },
            "&.MuiInputBase-root": { height: "fit-content !important" },
            display: "flex",
            flexGrow: 1,
          }}
          slotProps={{
            input: { sx: { height: "fit-content !important", alignItems: "start" }, maxLength: 4000 },
            formHelperText: { style: { display: "flex", justifyContent: "flex-end" } },
          }}
          rows={10}
          maxRows={15}
          size="large"
          className="input-fields"
          id="outlined-basic"
          type="text"
          variant="outlined"
          defaultValue={additionalText}
          onBlur={handleSubmit}
          multiline
        />
      );
    }
    
    if (shouldShowReadOnlyMessage(submissionData)) {
      return <p className="file-name-text">{submissionData.additionalInformation.comment}</p>;
    }
    
    return <NoDataPlaceholder />;
  };

  const renderFileDisplay = () => {
    if (!selectedFileName || !selectedFile) return null;
    
    return (
      <div className="main-document-div" style={{ marginTop: "10px !important" }}>
        <div className="color-filename-div">
          <div className="color-div">
            <InsertDriveFileIcon style={{ color: "#00924D", textAlign: "center" }} />
          </div>
          <div className="file-name-div">
            <p className="file-name-text">{selectedFileName}</p>
            <p className="file-size"><span>TXT</span>.<span>2 MB</span></p>
          </div>
        </div>
        <div className="download-delete-icon-container" style={{ display: "flex" }}>
          <div style={{ marginRight: "12px" }}>
            <a href={fileUrl} target="_blank" download aria-label="Get screenshot" style={{ cursor: "pointer" }}>
              <FileDownloadIcon style={{ color: "#12141599", cursor: "pointer" }} />
            </a>
          </div>
          <div>
            <DeleteIcon style={{ color: "#12141599", cursor: "pointer" }} onClick={deleteAttachment} />
          </div>
        </div>
      </div>
    );
  };

  const renderAttachmentSection = () => {
    const hasAttachmentData = hasAttachment();
    
    if (hasAttachmentData) {
      return (
        <div className={isNonDraftWithInfo() ? "additionalInformation-border" : ""}>
          <div className="main-document-div" style={{ marginBottom: "10px !important" }}>
            <div className="color-filename-div">
              <div className="color-div">
                <InsertDriveFileIcon style={{ color: "#00924D", textAlign: "center" }} />
              </div>
              <div className="file-name-text" style={{ paddingLeft: "5px !important" }}>
                {submissionData?.additionalInformation?.attachment?.fileName}
              </div>
            </div>
            <div className="download-delete-icon-container" style={{ display: "flex" }}>
              <div>
                <a 
                  href={submissionData.additionalInformation.attachment.url}
                  target="_blank"
                  download
                  aria-label="Get-additonal-document"
                  style={{ cursor: "pointer" }}
                >
                  <FileDownloadIcon style={{ color: "#12141599", cursor: "pointer" }} />
                </a>
              </div>
              <div>
                {isDraftSubmission() && (
                  <DeleteIcon
                    style={{ color: "#12141599", cursor: "pointer" }}
                    onClick={deleteAttachment}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (isNonDraftWithInfo()) {
      return (
        <div className="additionalInformation-border">
          <NoDataPlaceholder />
        </div>
      );
    }
    
    return null;
  };

  const containerWidth = isDraftSubmission() ? "95%" : "100%";
  const isNonDraft = isNonDraftWithInfo();
  const isDraft = isDraftSubmission();
  const showAddFileButton = isDraft && !hasAttachment();
  const showFileInstructions = isDraft;
  const showNewFileUpload = isDraft && !submissionData?.additionalInformation;
  const containerMarginTop = isNonDraft ? "20px" : "30px";

  return (
    <Container maxWidth="lg">
      <Grid2 container justifyContent="flex-start" alignItems="center">
        <Grid2 size={12}>
          <div className="additional-document" style={{ width: containerWidth }}>
            {/* Additional Information Section */}
            <div
              id="additonalInformation"
              className={isNonDraft ? "additonalInformation additionalInformation-border" : "additonalInformation"}
              style={{ marginTop: "30px" }}
            >
              <Typography
                sx={{
                  "font-weight": "bold !important",
                  "font-size": "20px !important",
                  color: "rgba(18, 20, 21, 1) !important",
                }}
                className="uploadSignedApkText"
              >
                Additional Information
              </Typography>
              {isDraft && (
                <p className="text-info-additional">
                  Any valuable information or instructions required to help with the QA process (e.g. credentials for a test access)
                </p>
              )}
              {renderAdditionalInfoContent()}
            </div>

            {/* Additional Document Section */}
            <div style={{ marginLeft: "10.9%", marginTop: containerMarginTop }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "100%",
                  borderBottom: isNonDraft ? "1px solid rgba(18, 20, 21, 0.2) !important" : "0px !important",
                }}
              >
                <Typography
                  sx={{
                    "font-weight": "bold !important",
                    "font-size": "20px !important",
                    color: "rgba(18, 20, 21, 1) !important",
                    "margin-left": "-5px !important",
                    marginTop: "15px !important",
                    marginBottom: "5px !important",
                  }}
                  className="uploadSignedApkText"
                >
                  Additional Document
                </Typography>
                {showAddFileButton && (
                  <div style={{ marginTop: "20px" }}>
                    <Button
                      onClick={handleButtonClick}
                      sx={{
                        height: "30px !important",
                        "font-weight": "400px !important",
                        "font-size": "12px !important",
                        "line-height": "19.6px",
                        color: "rgba(4, 118, 226, 1) !important",
                        border: "1px solid rgba(4, 118, 226, 1) !important",
                        textTransform: "none",
                      }}
                    >
                      <AddIcon />
                      Add File
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="fileInput"
                      onChange={handleAdditionalDocumentChange}
                      style={{ display: "none" }}
                    />
                  </div>
                )}
              </div>

              {/* File Upload Area */}
              <div className="file-uploader">
                {showFileInstructions && (
                  <p className="text-screenshots" style={{ marginTop: selectedFile ? 10 : 0 }}>
                    Any single file. When zip is uploaded it can contain any number of allowed file types. (Max file size limit 5MB)
                  </p>
                )}
                
                {showNewFileUpload ? (
                  <div>
                    <div>{renderFileDisplay()}</div>
                  </div>
                ) : (
                  <div id="additionalDocument" style={{ marginLeft: "-5px" }}>
                    {renderAttachmentSection()}
                  </div>
                )}
              </div>
            </div>

            {/* Error Dialog */}
            {dimensionError && (
              <PopupDialog
                onClose={handleCloseDialog}
                openErrorPopup={openErrorPopup}
                headerMessage={headerMessage}
                componentName={componentName}
              />
            )}
          </div>
        </Grid2>
      </Grid2>
    </Container>
  );
}

ApplicationDetailsAdditionalDocument.propTypes = {
  postCallTheNewSubmission: PropTypes.func.isRequired,
  submissionData: PropTypes.object.isRequired,
  appId: PropTypes.string.isRequired,
};

export default ApplicationDetailsAdditionalDocument;
